/** Maps a RevenueCat entitlement to the Membership UI state (trial, cancelled, etc). */
import type { CustomerInfo } from 'react-native-purchases';
import { getActiveMembershipEntitlement } from './RevenueCat';

export type StatusTone = 'success' | 'warning' | 'error';

export type AppleSubscriptionState =
  | 'trialing'
  | 'trial_cancelled'
  | 'active'
  | 'cancelled'
  | 'lifetime'
  | 'billing_issue';

export type AppleSubscription = {
  state: AppleSubscriptionState;
  productId: string;
  planName: string;
  periodEnd: Date | null;
  willRenew: boolean;
  isTrial: boolean;
  badge: { label: string; tone: StatusTone };
  statusLine: string;
  helpText: string;
};

export const STATUS_TONE_COLORS: Record<StatusTone, string> = {
  success: '#00B176',
  warning: '#B7791F',
  error: '#E52600',
};

const PRODUCT_NAMES: Record<string, string> = {
  os_normal_monthly: 'Open Studio · Monthly',
  os_normal_annual: 'Open Studio · Annual',
  os_pro_monthly: 'Open Studio Pro · Monthly',
  os_pro_annual: 'Open Studio Pro · Annual',
};

export function productDisplayName(productId?: string | null): string | null {
  if (!productId) return null;
  return PRODUCT_NAMES[productId] ?? productId;
}

function parseDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(value: Date | string | null | undefined): string {
  const d = value instanceof Date ? value : parseDate(value);
  if (!d) return '';
  try {
    return d.toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' });
  } catch {
    return d.toLocaleDateString();
  }
}

export function describeAppleSubscription(
  info: CustomerInfo | null,
): AppleSubscription | null {
  const entitlement = getActiveMembershipEntitlement(info);
  if (!entitlement) return null;

  const productId = entitlement.productIdentifier;
  const planName = productDisplayName(productId) ?? 'Open Studio membership';
  const periodEnd = parseDate(entitlement.expirationDate);
  const when = periodEnd ? formatDateTime(periodEnd) : null;
  const willRenew = Boolean(entitlement.willRenew);
  const isTrial = String(entitlement.periodType).toUpperCase() === 'TRIAL';
  const hasBillingIssue = Boolean(entitlement.billingIssueDetectedAt);

  const base = { productId, planName, periodEnd, willRenew, isTrial };

  if (hasBillingIssue) {
    return {
      ...base,
      state: 'billing_issue',
      badge: { label: 'Payment issue', tone: 'error' },
      statusLine: when
        ? `Payment failed · access ends ${when}`
        : 'Payment failed',
      helpText:
        "Apple couldn't charge your payment method. Update it in your Apple ID settings and your membership continues automatically.",
    };
  }

  if (!periodEnd && !willRenew) {
    return {
      ...base,
      state: 'lifetime',
      badge: { label: 'Active', tone: 'success' },
      statusLine: 'Lifetime access · nothing to renew',
      helpText:
        'This is a one-time purchase, so there is no renewal or cancellation to manage.',
    };
  }

  if (isTrial) {
    return willRenew
      ? {
          ...base,
          state: 'trialing',
          badge: { label: 'Free trial', tone: 'success' },
          statusLine: when ? `Free trial · first payment on ${when}` : 'Free trial',
          helpText:
            'You are not charged until the trial ends. Cancel in your Apple ID settings at least 24 hours before that time to avoid the first payment.',
        }
      : {
          ...base,
          state: 'trial_cancelled',
          badge: { label: 'Trial ending', tone: 'warning' },
          statusLine: when ? `Trial ends ${when} · will not convert` : 'Trial ending',
          helpText:
            'Auto-renew is off, so the trial will not turn into a paid plan. You keep access until it ends.',
        };
  }

  if (!willRenew) {
    return {
      ...base,
      state: 'cancelled',
      badge: { label: 'Cancelled', tone: 'warning' },
      statusLine: when ? `Cancelled · access until ${when}` : 'Cancelled',
      helpText:
        'You cancelled this plan, so it will not renew. You keep full access until the date above. Resubscribe before then to continue without a gap.',
    };
  }

  return {
    ...base,
    state: 'active',
    badge: { label: 'Active', tone: 'success' },
    statusLine: when ? `Renews on ${when}` : 'Active',
    helpText:
      'Billing is handled by Apple. Change or cancel anytime in your Apple ID subscription settings.',
  };
}
